import { AgentRegisterPayload } from '@src/v3/interfaces/app/auth/register';
import {
  AGENT_URLS,
  AUTH_API_URLS,
  LANDLORD_URLS,
  ONE_OFF_CREDIT,
  RENTER_URLS,
} from '@src/v3/constants/urls';
import { httpGet, httpPatch, httpPost } from '@src/v3/utils/http';
import {
  CheckEmailPayload,
  CheckPhonePayload,
  ExpressRenterRegisterPayload,
} from '@src/v3/interfaces/app/auth/register/renter/renterRegister';
import { RegisterRenterPayload } from '@src/v3/interfaces/components/wizard/register/details';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import { ZEME_TEMP_SESSION_TOKEN } from '@src/v3/constants/localStorage';
import {
  getRefreshToken,
  getUserId,
  setItem,
} from '@src/v3/utils/localStorage';

/**
 * Sends OTP to the provided phone number for login verification
 *
 * @param {string} phoneNumber - The phone number to send OTP to
 * @returns Promise
 */
// eslint-disable-next-line consistent-return
const sendOtp = async (phoneNumber: string) => {
  const payload = {
    identifier: phoneNumber,
    identifier_type: 'PHONE',
  };
  const response = await httpPost(AUTH_API_URLS.SEND_PHONE_OTP, payload);

  // Store session_token in localStorage if it exists in the response
  if (response && response.session_token) {
    setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
    return response;
  }
};

/**
 * Sends OTP to the provided phone number for login verification (new login flow)
 *
 * @param {string} phoneNumber - The phone number to send OTP to
 * @returns Promise
 */
// eslint-disable-next-line consistent-return
const sendLoginPhoneOtp = async (phoneNumber: string) => {
  const payload = {
    phone_number: phoneNumber,
  };
  const response = await httpPost(AUTH_API_URLS.LOGIN_PHONE_OTP, payload);

  // Store session_token in localStorage if it exists in the response
  if (response && response.session_token) {
    setItem(ZEME_TEMP_SESSION_TOKEN, response.session_token);
    return response;
  }
};

/**
 * Verifies OTP for the provided phone number and code
 *
 * @param {string} phoneNumber - The phone number to verify
 * @param {string} code - The OTP code to verify
 * @returns Promise
 */
const verifyOtp = async (
  phoneNumber: string,
  code: string,
  sessionToken?: string
) => {
  const payload = {
    phone_number: phoneNumber,
    code: code,
    session_token: sessionToken,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_OTP, payload);
  return response;
};

/**
 * Verifies OTP for login phone verification (new login flow)
 *
 * @param {string} phoneNumber - The phone number to verify
 * @param {string} code - The OTP code to verify
 * @param {string} sessionToken - Optional session token
 * @returns Promise
 */
const verifyLoginPhoneOtp = async (
  phoneNumber: string,
  code: string,
  sessionToken?: string
) => {
  const payload = {
    phone_number: phoneNumber,
    code: code,
    session_token: sessionToken,
  };
  const response = await httpPost(
    AUTH_API_URLS.VERIFY_LOGIN_PHONE_OTP,
    payload
  );
  return response;
};

/**
 * Sends email OTP for login verification (new login flow)
 *
 * @param {string} email - The email to send OTP to
 * @param {string} sessionToken - Session token from phone verification
 * @returns Promise
 */
// eslint-disable-next-line consistent-return
const sendLoginEmailOtp = async (email: string, sessionToken: string) => {
  const payload = {
    email: email,
    session_token: sessionToken,
  };
  const response = await httpPost(AUTH_API_URLS.LOGIN_EMAIL_OTP, payload);
  return response;
};

/**
 * Verifies email OTP for login verification (new login flow)
 *
 * @param {string} email - The email to verify
 * @param {string} code - The OTP code to verify
 * @param {string} sessionToken - Session token
 * @returns Promise
 */
const verifyLoginEmailOtp = async (
  email: string,
  code: string,
  sessionToken: string
) => {
  const payload = {
    email: email,
    code: code,
    session_token: sessionToken,
  };
  const response = await httpPost(
    AUTH_API_URLS.VERIFY_LOGIN_EMAIL_OTP,
    payload
  );
  return response;
};

/**
 * Sends magic link to the provided email address
 *
 * @param {string} emailAddress - The email address to send magic link to
 * @returns Promise
 */
const sendMagicLink = async (emailAddress: string, sessionToken?: string) => {
  const payload = {
    identifier: emailAddress,
    identifier_type: 'EMAIL',
    session_token: sessionToken,
  };
  const response = await httpPost(AUTH_API_URLS.SEND_MAGIC_LINK, payload);
  return response;
};

/**
 * Verifies magic link with email hash and token
 *
 * @param {string} emailHash - The email hash from the magic link
 * @param {string} token - The token from the magic link
 * @returns Promise
 */
const verifyMagicLink = async (
  emailHash: string,
  token: string,
  sessionToken?: string
) => {
  const payload = {
    email_hash: emailHash,
    token: token,
    session_token: sessionToken,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_MAGIC_LINK, payload);

  return response;
};

/**
 * Logs out the user by sending a POST request to the logout API endpoint.
 *
 * @return {Promise<object>} The response object from the logout API endpoint.
 */
const logoutUser = async () => {
  const params = {
    refresh_token: getRefreshToken(),
  };
  const response = await httpPost(AUTH_API_URLS.logout, params);
  return response;
};

/**
 * Deletes a user by sending a request to the server with the OTP code.
 *
 * @param {string} code - The OTP code for verification
 * @return {Promise<any>} The response from the server after deleting the user
 */
const deleteUser = async (code: string) => {
  const params = {
    code,
  };
  const response = await httpPost(AUTH_API_URLS.DELETE_USER, params);
  return response;
};

/**
 * This method is used to perform API request for register an agent
 *
 * @param {String} params.general_information.first_name
 * @param {String} params.general_information.middle_name
 * @param {String} params.general_information.last_name
 * @param {String} params.general_information.phone_number
 * @param {Boolean} params.general_information.verified
 * @param {String} params.general_information.email
 * @param {String} params.general_information.password
 * @param {String} params.general_information.notifications_token
 * @param {String} params.agent_information.profile_picture
 * @param {String} params.agent_information.company_name
 * @returns Promise
 */
const registerAgent = async (
  params: AgentRegisterPayload,
  isLandlord: boolean
) => {
  const response = await httpPost(
    isLandlord ? AUTH_API_URLS.REGISTER_LANDLORD : AUTH_API_URLS.REGISTER_AGENT,
    params
  );
  return response;
};

/**
 * This method is used to perform API request for register renter
 *
 * @param {GeneralInformation} params.general_information
 * @param {RenterInformation} params.renter_information
 * @returns Promise
 */
const registerRenter = async (
  params: RegisterRenterPayload | ExpressRenterRegisterPayload
) => {
  const response = await httpPost(AUTH_API_URLS.REGISTER_RENTER, params);
  return response;
};

/**
 * This method is used to perform API request for update [patch] renter
 *
 * @param {GeneralInformation} params.general_information
 * @param {RenterInformation} params.renter_information
 * @param {string} id
 * @returns Promise
 */
const editRenter = async (
  params: Partial<RegisterRenterPayload>,
  id: string
) => {
  const response = await httpPatch(
    AUTH_API_URLS.EDIT_RENTER.replace('#RENTER_ID#', String(id || '')),
    params
  );
  return response;
};

/**
 * This method is used to perform API request for update [patch] agent
 *
 * @param {AgentRegisterPayload} params
 * @param {string} id
 * @returns Promise
 */
const editAgent = async (
  params: Partial<RegisterRenterPayload>,
  id: string
) => {
  const response = await httpPatch(
    AUTH_API_URLS.EDIT_AGENT.replace('#AGENT_ID#', String(id || '')),
    params
  );
  return response;
};

/**
 * This method is used to perform API request for update [patch] agent
 *
 * @param {AgentRegisterPayload} params
 * @param {string} id
 * @returns Promise
 */
const editLandlord = async (
  params: Partial<RegisterRenterPayload>,
  id: string
) => {
  const response = await httpPatch(
    AUTH_API_URLS.EDIT_LANDLORD.replace('#LANDLORD_ID#', String(id || '')),
    params
  );
  return response;
};

/**
 * This method is used to perform API request check email is exist or not
 * @param {CheckEmailPayload} params.email
 * @returns Promise
 */
const checkEmailExist = async (params: CheckEmailPayload) => {
  const response = await httpPost(AUTH_API_URLS.CHECK_EMAIL, params);
  return response;
};

/**
 * This method is used to perform API request check Phone number is exist or not
 * @param {CheckEmailPayload} params.phone
 * @returns Promise
 */
const checkPhoneExist = async (params: CheckPhonePayload) => {
  const response = await httpPost(AUTH_API_URLS.CHECK_PHONE, params);
  return response;
};

/**
 * This method is used to perform API request to send OTP to user.
 * @param {params} params.phone_number
 * @returns Promise
 */
const updatePhoneNumber = async (params: { phone_number: string }) => {
  const response = await httpPost(AUTH_API_URLS.UPDATE_PHONE_NUMBER, params);
  return response;
};

/**
 * This method is used to perform API request to send OTP to user.
 * @param {params} params.phone_number
 * @returns Promise
 */
const sendOtpToUser = async (params: { phone_number: string }) => {
  const response = await httpPost(AUTH_API_URLS.SEND_OTP, params);
  return response;
};

/**
 * This method is used to perform API request to verify OTP.
 * @param {params} params.phone_number
 * @param {params} params.code
 * @returns Promise
 */
const verifyOtpForUser = async (params: {
  phone_number: string;
  code: string;
}) => {
  const response = await httpPost(AUTH_API_URLS.VERIFY_OTP, params);
  return response;
};

/**
 * This method is used to perform API request to send OTP to verifyone off credit user OTP .
 * @param {params} params.phone_number
 * @returns Promise
 */
const sendOtpToOneOffUser = async (params: { phone_number: string }) => {
  const response = await httpPost(ONE_OFF_CREDIT.SEND_OTP, params);
  return response;
};

/**
 * This method is used to perform API request to verify OTP for one off credit user.
 * @param {params} params.phone_number
 * @param {params} params.code
 * @param {params} params.url_key
 * @returns Promise
 */
const verifyOtpForOneOffUser = async (params: {
  phone_number: string;
  code: string;
  url_key?: string;
}) => {
  const response = await httpPost(ONE_OFF_CREDIT.VERIFY_OTP, params);
  return response;
};

/**
 * This method is used to perform API request to get active user profile.
 * @param {string} userType
 * @param {string} userId
 * @returns Promise
 */
const getUserProfile = async (userType: string, userId: string) => {
  let url = '';

  // Get user profile for Renter, Landlord and Agent.
  switch (userType) {
    case ZEME_USERS_ROLE.AGENT:
      url = AGENT_URLS.GET_PROFILE;
      break;
    case ZEME_USERS_ROLE.LANDLORD:
      url = LANDLORD_URLS.GET_PROFILE;
      break;

    default:
      url = RENTER_URLS.GET_PROFILE;
      break;
  }

  // Bind agent/renter id in URL.
  url = url.replace('#ID#', String(userId));

  const response = await httpGet(url);
  return response;
};

/**
 * Retrieves the public profile of an agent based on their ID.
 *
 * @param {string} id - The ID of the agent.
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const getAgentPublicProfile = async (id: string) => {
  const url = AGENT_URLS.AGENT_PUBLIC_PROFILE.replace('#ID#', String(id));

  const response = await httpGet(url);
  return response;
};

/**
 * This method is used to perform API request to update renter profile.
 * @returns Promise
 */
const updateKidsAndPets = async (params: {
  renter_information: {
    household_information: {
      total_occupants: number;
      small_dogs: number;
      medium_dogs: number;
      large_dogs: number;
      cats: number;
      other_pets: number;
      total_adults?: number;
      total_children?: number;
    };
  };
}) => {
  const id = getUserId();

  const response = await httpPatch(
    `${RENTER_URLS.UPDATE_PROFILE.replace('#ID#', String(id))}`,
    params
  );
  return response;
};

/**
 * This method is used to perform API request to get subscription plans
 * @returns Promise
 */
const fetchSubscriptionPlans = async () => {
  const url = `${AUTH_API_URLS.GET_SUBSCRIPTION_PLANS}`;

  const response = await httpGet(url);
  return response;
};

/**
 * Applies a coupon to a specific plan.
 *
 * @param {string} planId - The ID of the plan to apply the coupon to.
 * @param {string} code - The coupon code to apply.
 * @return {Promise<any>} A promise that resolves with the response from the server.
 */
const applyCoupon = async (planId: string, code: string) => {
  const params = {
    plan_id: planId,
    coupon_code: code,
  };
  const url = `${AUTH_API_URLS.APPLY_COUPON}`;

  const response = await httpPost(url, params, { hideError: true });
  return response;
};

/**
 * Creates an agent subscription by applying a coupon code to a plan.
 *
 * @param {string} planId - The ID of the plan.
 * @param {string} couponCode - The coupon code to apply.
 * @param {string} token - The token to apply finix payment.
 * @return {Promise<any>} - The response from the server.
 */
const createAgentSubscription = async (
  planId: string,
  couponCode: string | null | undefined,
  token?: string,
  type?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  address?: any
) => {
  const params: {
    plan_id: string;
    coupon_code?: string;
    token?: string;
    type?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    address?: any;
  } = {
    plan_id: planId,
  };

  if (couponCode) {
    params.coupon_code = couponCode;
  }

  if (token) {
    params.token = token;
  }

  if (type) {
    params.type = type;
  }

  if (address) {
    params.address = {
      line1: address.address1,
      city: address.locality,
      region: address.administrativeArea,
      country: address.countryCode,
      postal_code: address.postalCode,
    };
  }

  const url = `${AUTH_API_URLS.CREATE_AGENT_SUBSCRIPTION}`;

  const response = await httpPost(url, params);
  return response;
};

/**
 * Fetches a token with all permissions.
 *
 * @return {Promise<any>} The response from the server.
 */
const fetchTokenWithAllPermissions = async () => {
  const url = `${AUTH_API_URLS.GET_UNLIMITED_ACCESS_TOKEN}`;

  const response = await httpPost(url, {});
  return response;
};

/**
 * Fetch Fair Housing Template from API
 *
 */
const getFairHousingTemplate = async (agentId: string) => {
  const url = AUTH_API_URLS.GET_FAIR_HOUSING_TEMPLATE;

  const params = {
    agent_id: agentId,
  };

  const response = await httpPost(url, params);

  return response;
};

const loginWithGoogle = async (token: string) => {
  const url = AUTH_API_URLS.LOGIN_WITH_GOOGLE;
  const params = {
    token,
  };
  const response = await httpPost(url, params);
  return response;
};

const loginWithApple = async (
  token: string,
  firstName: string,
  lastName: string,
  email: string
) => {
  const url = AUTH_API_URLS.LOGIN_WITH_APPLE;
  const params: {
    token: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  } = {
    token,
  };

  if (firstName) params['first_name'] = firstName;

  if (lastName) params['last_name'] = lastName;

  if (email) params['email'] = email;

  const response = await httpPost(url, params);
  return response;
};

/**
 * Verifies OAuth OTP for the provided phone number and code
 *
 * @param {string} phoneNumber - The phone number to verify
 * @param {string} code - The OTP code to verify
 * @returns Promise
 */
const verifyOAuthOtp = async (phoneNumber: string, code: string) => {
  const payload = {
    phone_number: phoneNumber,
    code: code,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_OAUTH_OTP, payload);
  return response;
};

/**
 * Sends OTP to the provided phone number for signup verification
 *
 * @param {string} phoneNumber - The phone number to send OTP to
 * @param {string} userType - The type of user (renter, agent, landlord)
 * @returns Promise
 */
const sendSignupOtp = async (phoneNumber: string, userType: string) => {
  const payload = {
    phone_number: phoneNumber,
    user_type: userType,
  };
  const response = await httpPost(AUTH_API_URLS.SIGN_UP_PHONE, payload);
  return response;
};

const sendBasicPhoneOtp = async (phoneNumber: string, userType: string) => {
  const payload = {
    phone_number: phoneNumber,
    user_type: userType,
  };
  const response = await httpPost(AUTH_API_URLS.SEND_BASIC_PHONE_OTP, payload);
  return response;
};

/**
 * Requests an email change for the user
 *
 * @param {string} newEmail - The new email address
 * @returns Promise
 */
const requestEmailChange = async (newEmail: string) => {
  const payload = {
    new_email: newEmail,
  };
  const response = await httpPost(AUTH_API_URLS.REQUEST_EMAIL_CHANGE, payload);
  return response;
};

/**
 * Verifies OTP for signup with the provided phone number and code
 *
 * @param {string} phoneNumber - The phone number to verify
 * @param {string} code - The OTP code to verify
 * @param {string} userType - The type of user (renter, agent, landlord)
 * @returns Promise
 */
const verifySignupOtp = async (
  phoneNumber: string,
  code: string,
  userType: string
) => {
  const payload = {
    phone_number: phoneNumber,
    code: code,
    user_type: userType,
  };
  const response = await httpPost(AUTH_API_URLS.SIGN_UP_PHONE_VERIFY, payload);
  return response;
};

/**
 * Completes renter signup with user details
 *
 * @param {string} firstName - The user's first name
 * @param {string} lastName - The user's last name
 * @param {string} email - The user's email address
 * @param {string} phoneNumber - The user's verified phone number
 * @returns Promise
 */
const completeRenterSignup = async (
  firstName: string,
  middleName: string,
  lastName: string,
  email: string,
  phoneNumber: string
) => {
  const payload = {
    general_information: {
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
      email: email,
      phone_number: phoneNumber,
    },
  };

  const response = await httpPost(
    AUTH_API_URLS.COMPLETE_RENTER_SIGNUP,
    payload
  );
  return response;
};

/**
 * Completes agent signup with user details and work details
 *
 * @param {string} firstName - The user's first name
 * @param {string} middleName - The user's middle name (optional)
 * @param {string} lastName - The user's last name
 * @param {string} licenseNumber - The user's license number (optional)
 * @param {string} email - The user's email address
 * @param {string} phoneNumber - The user's verified phone number
 * @param {string} companyName - The company name
 * @param {string} companyAddressLine1 - The company address line 1
 * @param {string} companyAddressLine2 - The company address line 2 (optional)
 * @param {string} companyPhoto - The company photo URL/base64 (optional)
 * @returns Promise
 */
const completeAgentSignup = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  companyName: string,
  companyAddressLine1: string,
  middleName?: string,
  licenseNumber?: string,
  companyAddressLine2?: string,
  companyAddressPlaceId?: string,
  companyPhoto?: string
) => {
  const generalInformation: Record<string, unknown> = {
    first_name: firstName,
    last_name: lastName,
    email: email,
    phone_number: phoneNumber,
  };

  if (middleName) {
    generalInformation.middle_name = middleName;
  }

  const agentInformation: Record<string, unknown> = {
    company_name: companyName,
    company_primary_address: companyAddressLine1,
  };

  if (licenseNumber) {
    agentInformation.license_number = licenseNumber;
  }

  if (companyAddressLine2) {
    agentInformation.company_address_line_two = companyAddressLine2;
  }

  if (companyAddressPlaceId) {
    agentInformation.company_address_placeid = companyAddressPlaceId;
  }

  if (companyPhoto) {
    agentInformation.profile_picture = companyPhoto;
  }

  const payload = {
    general_information: generalInformation,
    agent_information: agentInformation,
  };

  const response = await httpPost(AUTH_API_URLS.COMPLETE_AGENT_SIGNUP, payload);
  return response;
};

/**
 * Completes landlord signup with user details and work details
 *
 * @param {string} firstName - The user's first name
 * @param {string} middleName - The user's middle name (optional)
 * @param {string} lastName - The user's last name
 * @param {string} email - The user's email address
 * @param {string} phoneNumber - The user's verified phone number
 * @param {string} companyName - The company name
 * @param {string} companyAddressLine1 - The company address line 1
 * @param {string} companyAddressLine2 - The company address line 2 (optional)
 * @param {string} companyAddressPlaceId - The company address place ID (optional)
 * @param {string} companyPhoto - The company photo URL/base64 (optional)
 * @returns Promise
 */
const completeLandlordSignup = async (
  firstName: string,
  lastName: string,
  email: string,
  phoneNumber: string,
  companyName: string,
  companyAddressLine1: string,
  middleName?: string,
  companyAddressLine2?: string,
  companyAddressPlaceId?: string,
  companyPhoto?: string
) => {
  const generalInformation: Record<string, unknown> = {
    first_name: firstName,
    last_name: lastName,
    email: email,
    phone_number: phoneNumber,
  };

  if (middleName) {
    generalInformation.middle_name = middleName;
  }

  const landlordInformation: Record<string, unknown> = {
    company_name: companyName,
    company_primary_address: companyAddressLine1,
  };

  if (companyAddressLine2) {
    landlordInformation.company_address_line_two = companyAddressLine2;
  }

  if (companyAddressPlaceId) {
    landlordInformation.company_address_placeid = companyAddressPlaceId;
  }

  if (companyPhoto) {
    landlordInformation.profile_picture = companyPhoto;
  }

  const payload = {
    general_information: generalInformation,
    landlord_information: landlordInformation,
  };

  const response = await httpPost(
    AUTH_API_URLS.COMPLETE_LANDLORD_SIGNUP,
    payload
  );
  return response;
};

/**
 * Verifies user email by sending verification link
 *
 * @param {string} email - The email address to verify
 * @returns Promise
 */
const verifyUser = async (email: string) => {
  const payload = {
    email: email,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_USER, payload);
  return response;
};

/**
 * Confirms user email verification with token
 *
 * @param {string} token - The verification token
 * @returns Promise
 */
const verifyUserConfirm = async (token: string, email: string) => {
  const payload = {
    token: token,
    email: email,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_USER_CONFIRM, payload);
  return response;
};

/**
 * Confirms user email change with token
 *
 * @param {string} token - The verification token
 * @returns Promise
 */
const verifyEmailChange = async (
  tokenOrCode: string,
  isOtp: boolean = false
) => {
  const payload = isOtp ? { code: tokenOrCode } : { token: tokenOrCode };
  const response = await httpPost(AUTH_API_URLS.VERIFY_EMAIL_CHANGE, payload);
  return response;
};

/**
 * Requests a phone number change for the user
 *
 * @param {string} newPhoneNumber - The new phone number
 * @returns Promise
 */
const requestPhoneChange = async (newPhoneNumber: string) => {
  const payload = {
    new_phone_number: newPhoneNumber,
  };
  const response = await httpPost(AUTH_API_URLS.REQUEST_PHONE_CHANGE, payload);
  return response;
};

/**
 * Verifies phone change with the OTP code
 *
 * @param {string} code - The OTP code
 * @returns Promise
 */
const verifyPhoneChange = async (code: string) => {
  const payload = {
    code: code,
  };
  const response = await httpPost(AUTH_API_URLS.VERIFY_PHONE_CHANGE, payload);
  return response;
};

/**
 * Sends OTP to the provided email for signup verification
 *
 * @param {string} email - The email address to send OTP to
 * @returns Promise
 */
const sendSignupEmailOtp = async (email: string) => {
  const payload = {
    email: email,
  };
  const response = await httpPost(AUTH_API_URLS.SIGNUP_EMAIL_OTP, payload);
  return response;
};

/**
 * Verifies OTP for signup email verification
 *
 * @param {string} email - The email address to verify
 * @param {string} code - The OTP code to verify
 * @returns Promise
 */
const verifySignupEmailOtp = async (email: string, code: string) => {
  const payload = {
    email: email,
    code: code,
  };
  const response = await httpPost(
    AUTH_API_URLS.VERIFY_SIGNUP_EMAIL_OTP,
    payload
  );
  return response;
};

/**
 * Looks up an agent by email to check if they exist in the system.
 *
 * @param {string} email - The email address to look up
 * @returns Promise with agent lookup response
 */
const lookupAgentByEmail = async (email: string) => {
  const url = `${AUTH_API_URLS.AGENT_LOOKUP}?email=${encodeURIComponent(
    email
  )}`;
  const response = await httpGet(url, { hideError: true });
  return response;
};

export {
  sendOtp,
  sendLoginPhoneOtp,
  sendLoginEmailOtp,
  verifyOtp,
  verifyLoginPhoneOtp,
  verifyLoginEmailOtp,
  sendMagicLink,
  verifyMagicLink,
  logoutUser,
  deleteUser,
  completeAgentSignup,
  completeLandlordSignup,
  registerAgent,
  checkEmailExist,
  checkPhoneExist,
  registerRenter,
  editRenter,
  editAgent,
  editLandlord,
  getUserProfile,
  getAgentPublicProfile,
  updateKidsAndPets,
  sendOtpToUser,
  verifyOtpForUser,
  updatePhoneNumber,
  fetchSubscriptionPlans,
  applyCoupon,
  createAgentSubscription,
  fetchTokenWithAllPermissions,
  getFairHousingTemplate,
  loginWithGoogle,
  loginWithApple,
  sendOtpToOneOffUser,
  verifyOtpForOneOffUser,
  verifyOAuthOtp,
  sendSignupOtp,
  sendBasicPhoneOtp,
  verifySignupOtp,
  completeRenterSignup,
  sendSignupEmailOtp,
  verifySignupEmailOtp,
  verifyUser,
  verifyUserConfirm,
  requestEmailChange,
  verifyEmailChange,
  requestPhoneChange,
  verifyPhoneChange,
  lookupAgentByEmail,
};
